import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Plus, Trash2, LogOut, Camera, RefreshCw, ExternalLink, Edit3, X, Check, Calendar } from 'lucide-react';
import {
  getAlumniGallery,
  addAlumniPhoto,
  editAlumniPhoto,
  deleteAlumniPhoto,
  getToken,
  clearToken,
  me,
} from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

// Alumni that have a gallery slug set in mock.js — matches the backend seed
const KNOWN_ALUMNI = [
  { slug: 'paul-hammond', name: 'Paul Hammond' },
  { slug: 'nick-scott', name: 'Nick Scott' },
  { slug: 'jamari-venter', name: 'Jamari Venter' },
  { slug: 'bryquice-brown', name: 'Bryquice Brown' },
];

export default function AdminAlumniGallery() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [authOk, setAuthOk] = useState(null);
  const [slug, setSlug] = useState(KNOWN_ALUMNI[0].slug);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPhoto, setNewPhoto] = useState({ url: '', caption: '' });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ url: '', caption: '' });

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const g = await getAlumniGallery(slug);
      setGallery(g);
    } catch (err) {
      toast({ title: 'Load failed', description: err.message });
    } finally {
      setLoading(false);
    }
  }, [slug, toast]);

  useEffect(() => { if (authOk) load(); }, [authOk, load]);

  if (authOk === false) return <Navigate to="/admin/login" replace />;
  if (authOk === null) return null;

  const logout = () => { clearToken(); nav('/admin/login'); };

  const addPhoto = async (e) => {
    e.preventDefault();
    if (!newPhoto.url.trim()) { toast({ title: 'Photo URL required' }); return; }
    try {
      const g = await addAlumniPhoto(slug, newPhoto);
      setGallery(g);
      setNewPhoto({ url: '', caption: '' });
      toast({ title: 'Photo added' });
    } catch (err) { toast({ title: 'Add failed', description: err.message }); }
  };

  const startEdit = (p) => { setEditingId(p.id); setEditDraft({ url: p.url, caption: p.caption || '' }); };

  const saveEdit = async () => {
    try {
      const g = await editAlumniPhoto(slug, editingId, editDraft);
      setGallery(g);
      setEditingId(null);
      toast({ title: 'Photo updated' });
    } catch (err) { toast({ title: 'Update failed', description: err.message }); }
  };

  const removePhoto = async (id, caption) => {
    if (!window.confirm(`Remove "${caption || 'photo'}"?`)) return;
    try {
      const g = await deleteAlumniPhoto(slug, id);
      setGallery(g);
      if (editingId === id) setEditingId(null);
      toast({ title: 'Photo removed' });
    } catch (err) { toast({ title: 'Remove failed', description: err.message }); }
  };

  const currentIdx = gallery?.currentIndex ?? 0;
  const nextRotation = gallery?.nextRotation ? new Date(gallery.nextRotation) : null;

  return (
    <div className="min-h-screen bg-[#f5f6f8] font-body">
      <div className="bg-[#0a1f44] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto object-contain" />
            <div>
              <div className="font-display text-xl tracking-wide">ADMIN CMS</div>
              <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Alumni Photo Gallery</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/alumni" target="_blank" data-testid="view-live-btn" className="hidden md:inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest text-white/70 hover:text-white">
              <ExternalLink className="w-4 h-4" /> View Live
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Alumni selector */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/60">Alumni:</span>
          {KNOWN_ALUMNI.map((a) => (
            <button
              key={a.slug}
              onClick={() => setSlug(a.slug)}
              data-testid={`alumni-tab-${a.slug}`}
              className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 ${slug === a.slug ? 'bg-[#0a1f44] text-white border-[#0a1f44]' : 'bg-white text-[#0a1f44] border-[#0a1f44]/20 hover:border-[#c8102e]'}`}
            >
              {a.name}
            </button>
          ))}
        </div>

        {loading || !gallery ? (
          <div className="text-center py-16 text-[#0a1f44]/50">Loading…</div>
        ) : (
          <>
            {/* Rotation status */}
            <div className="bg-white shadow-sm p-6 flex flex-wrap items-center justify-between gap-4" data-testid="rotation-status">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-[#c8102e]" />
                <div>
                  <div className="font-heading uppercase tracking-[0.3em] text-[10px] text-[#c8102e]">Auto-Rotation</div>
                  <div className="font-display text-xl text-[#0a1f44]">
                    Photo {gallery.photos.length > 0 ? currentIdx + 1 : 0} of {gallery.photos.length} shown this month
                  </div>
                  {nextRotation && gallery.photos.length > 1 && (
                    <div className="text-sm text-[#0a1f44]/70 mt-1">
                      Next rotation: <strong>{nextRotation.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={load} className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reload</button>
            </div>

            {/* Add new photo */}
            <form onSubmit={addPhoto} className="bg-white shadow-sm p-6 space-y-3" data-testid="add-photo-form">
              <h2 className="font-display text-xl text-[#0a1f44]">ADD PHOTO</h2>
              <div className="grid md:grid-cols-[2fr_1fr_auto] gap-2">
                <input type="url" placeholder="Photo URL (e.g. https://…/photo.webp) *" value={newPhoto.url} onChange={(e) => setNewPhoto({ ...newPhoto, url: e.target.value })} data-testid="add-photo-url" className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
                <input type="text" placeholder="Caption (optional)" value={newPhoto.caption} onChange={(e) => setNewPhoto({ ...newPhoto, caption: e.target.value })} data-testid="add-photo-caption" className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
                <button type="submit" data-testid="add-photo-btn" className="inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-2.5">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
              <p className="text-[11px] text-[#0a1f44]/50">💡 Upload photos to the Emergent Assets tool first, then paste the public URL here.</p>
            </form>

            {/* Photo grid */}
            <div className="bg-white shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl text-[#0a1f44]">GALLERY · {gallery.photos.length} photo{gallery.photos.length !== 1 ? 's' : ''}</h2>
              </div>
              {gallery.photos.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-[#0a1f44]/15 text-[#0a1f44]/60" data-testid="gallery-empty">
                  No photos yet. Add the first one above.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
                  {gallery.photos.map((p, idx) => (
                    <div key={p.id} data-testid={`photo-card-${p.id}`} className={`border-2 ${idx === currentIdx ? 'border-[#c8102e]' : 'border-[#0a1f44]/10'} flex flex-col`}>
                      <div className="aspect-[3/4] bg-[#0a1f44] overflow-hidden relative">
                        <img src={p.url} alt={p.caption || 'photo'} className="w-full h-full object-cover" />
                        {idx === currentIdx && (
                          <div className="absolute top-2 left-2 bg-[#c8102e] text-white text-[10px] font-heading uppercase tracking-widest px-2 py-1" data-testid="current-photo-badge">
                            ★ This month
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-white/90 text-[#0a1f44] text-[10px] font-heading uppercase tracking-widest px-2 py-1">#{idx + 1}</div>
                      </div>
                      <div className="p-3 flex-1 flex flex-col gap-2">
                        {editingId === p.id ? (
                          <>
                            <input value={editDraft.url} onChange={(e) => setEditDraft({ ...editDraft, url: e.target.value })} data-testid="edit-photo-url" className="border border-[#c8102e] px-2 py-1 text-xs" placeholder="URL" />
                            <input value={editDraft.caption} onChange={(e) => setEditDraft({ ...editDraft, caption: e.target.value })} data-testid="edit-photo-caption" className="border border-[#c8102e] px-2 py-1 text-xs" placeholder="Caption" />
                            <div className="flex gap-1">
                              <button onClick={saveEdit} data-testid={`save-photo-${p.id}`} className="flex-1 py-1.5 bg-[#c8102e] text-white text-xs font-heading uppercase tracking-widest inline-flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Save</button>
                              <button onClick={() => setEditingId(null)} data-testid={`cancel-photo-${p.id}`} className="py-1.5 px-3 border border-[#0a1f44]/30 text-xs"><X className="w-3 h-3" /></button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm text-[#0a1f44] flex-1 line-clamp-2">{p.caption || <span className="text-[#0a1f44]/40 italic">No caption</span>}</div>
                            <div className="flex gap-1">
                              <button onClick={() => startEdit(p)} data-testid={`edit-photo-btn-${p.id}`} className="flex-1 py-1.5 border border-[#0a1f44]/30 hover:border-[#c8102e] text-xs inline-flex items-center justify-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                              <button onClick={() => removePhoto(p.id, p.caption)} data-testid={`delete-photo-btn-${p.id}`} className="py-1.5 px-3 border border-[#c8102e] text-[#c8102e] hover:bg-[#c8102e] hover:text-white text-xs"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
